import { Component, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { ClientService } from '../../../core/services/client.service';
import { FormValidators } from '../../../core/validators/form-validators';
import { TaskNotificationService } from '../../../core/services/task-notification.service';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-client-create',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, MatIconModule],
  templateUrl: './client-create.component.html',
  styleUrls: [`client-create.component.css`],
})
export class ClientCreateComponent {
  private fb = inject(FormBuilder);
  private clientService = inject(ClientService);
  private router = inject(Router);
  private taskNotificationService = inject(TaskNotificationService);

  isLoading = signal(false);
  errorMessage = signal<string | null>(null);
  selectedFile = signal<File | null>(null);
  previewDataUrl = signal<string | null>(null);
  isDragOver = signal(false);

  clientForm = this.fb.group({
    numeroClient: ['', [Validators.required, FormValidators.numeroClient()]],
    siret: ['', [FormValidators.siret()]],
    denomination: ['', [Validators.required]],
    adresseFacturation: ['', [Validators.required]],
    ville: ['', [Validators.required]],
    codePostal: ['', [Validators.required, FormValidators.codePostalFr()]]
  });

  onFileSelected(event: any) {
    const file = event.target.files?.[0] as File;
    if (file) this.setSelectedFile(file);
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver.set(true);
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (file) this.setSelectedFile(file);
  }

  removeSelectedFile() {
    this.selectedFile.set(null);
    this.previewDataUrl.set(null);
  }

  private setSelectedFile(file: File) {
    this.selectedFile.set(file);
    const reader = new FileReader();
    reader.onload = () => (this.previewDataUrl.set(reader.result as string));
    reader.readAsDataURL(file);
  }

  onSubmit() {
    // Marque tous les champs comme touchés afin d'afficher les erreurs immédiatement
    this.clientForm.markAllAsTouched();
    if (this.clientForm.invalid) {
      return;
    }

    if (this.clientForm.valid) {
      this.isLoading.set(true);
      this.errorMessage.set(null);

      this.clientService.createClient(this.clientForm.value as any).subscribe({
        next: () => {
          const numero = this.clientForm.value.numeroClient as string;
          if (this.selectedFile() && numero) {
            // récupérer l'ID du client fraîchement créé via la recherche
            this.clientService.getClients(numero, 0, 1).subscribe({
              next: (page) => {
                const created = page.content?.find(c => c.numeroClient === numero) || page.content?.[0];
                if (created?.id) {
                  const taskId = this.taskNotificationService.start('Photo client', 'Upload de l\'image en cours...');
                  this.clientService.uploadPhotoAsync(created.id, this.selectedFile() as File).pipe(
                    switchMap(job => this.clientService.waitForPhotoUpload(created.id!, job.jobId))
                  ).subscribe({
                    next: () => {
                      this.taskNotificationService.succeed(taskId, 'Image uploadée.');
                      this.finishSuccess();
                    },
                    error: (err) => {
                      this.taskNotificationService.fail(taskId, 'L\'upload de l\'image a échoué.');
                      this.handleError(err, 'La photo a été créée mais l\'upload a échoué.');
                    }
                  });
                } else {
                  // pas d'ID trouvé, on termine quand même
                  this.finishSuccess();
                }
              },
              error: () => this.finishSuccess() // si la recherche échoue, on redirige quand même
            });
          } else {
            this.finishSuccess();
          }
        },
        error: (err) => this.handleError(err, 'Une erreur est survenue lors de la création du client.')
      });
    }
  }

  private finishSuccess() {
    this.isLoading.set(false);
    this.router.navigate(['/clients']);
  }

  private handleError(err: any, fallbackMessage: string) {
    this.errorMessage.set(err?.error?.message || err?.error || fallbackMessage);
    this.isLoading.set(false);
  }
}
